/**
 * Erkennt den Vertragswert `OWNER_MUST_TRANSFER` im `.message` eines
 * `PostgrestError` -- GENAU den einen Pfad, ueber den `GoogleAccounts.tsx` den
 * DB-Trigger `guard_owner_grant_delete` sieht (Migration
 * `20260923110000_merchant_center_members.sql`, feuert bei jedem `DELETE FROM
 * google_accounts`, direkt wie kaskadiert): Postgres' `RAISE EXCEPTION
 * 'OWNER_MUST_TRANSFER'` wird woertlich zur `.message`, und `PostgrestError`
 * extends `Error` (siehe `node_modules/@supabase/postgrest-js/src/PostgrestError.ts`).
 *
 * NICHT geeignet fuer eine echte `FunctionsHttpError` aus
 * `supabase.functions.invoke(...)` (der Pfad, den `GeneralTab.tsx` fuer
 * `delete-user-account` benutzt): deren `.message` ist konstant die generische
 * Zeichenkette "Edge Function returned a non-2xx status code"
 * (`node_modules/@supabase/functions-js/dist/main/types.js`), und der
 * geparste Body steckt nicht in `.context.message`/`.context.error`, sondern
 * `.context` IST die rohe `Response` -- der Body muss per `await
 * .context.json()` gelesen werden. Dafuer gibt es `readOwnerMustTransfer`
 * (async, siehe unten). Fix-Runde 1: diese Funktion pruefte vorher zusaetzlich
 * zwei `.context`-Formen (`.context.message`, `.context.error`), die bei
 * einer echten `FunctionsHttpError` nie vorkommen -- erfundenes Verhalten,
 * nie beobachtet, entfernt.
 */
export function isOwnerMustTransferError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;

  const message = (err as { message?: unknown }).message;
  return typeof message === 'string' && message.includes('OWNER_MUST_TRANSFER');
}

/** Minimalsicht auf das, was `readOwnerMustTransfer` von `err.context` braucht. */
interface ResponseLike {
  status?: unknown;
  json?: () => Promise<unknown>;
  clone?: () => ResponseLike;
}

/**
 * Erkennt `OWNER_MUST_TRANSFER` im Pfad, den `isOwnerMustTransferError` NICHT
 * abdeckt: eine echte `FunctionsHttpError` aus `supabase.functions.invoke(...)`
 * (`GeneralTab.tsx`s Aufruf von `delete-user-account`). `err.context` ist dort
 * die rohe `Response` -- `status` steht synchron zur Verfuegung, der Body nur
 * ueber `await .json()`.
 *
 * ASYNC, weil das Lesen des Bodys ASYNC ist -- es gibt hier keinen
 * synchronen Ersatz.
 *
 * `status !== 409` bricht VOR jedem `.json()`-Aufruf ab: ein anderer
 * Fehlerstatus hat mit diesem Vertragswert nichts zu tun, und ein
 * Response-Body ist nur EINMAL lesbar. Aus genau diesem Grund wird `.clone()`
 * benutzt, wenn vorhanden -- ein zweiter Leser (z.B. Sentry, oder ein
 * generischer 409-Zweig, der denselben `error` noch anfasst) darf nicht auf
 * einen bereits konsumierten Stream treffen.
 *
 * Liefert `null` bei jedem Fehlschlag (kein Objekt, kein `.context`, falscher
 * Status, kein `.json`, Parse-Fehler, kein `error === 'OWNER_MUST_TRANSFER'`
 * im Body) -- nie geworfen, damit ein Aufrufer im `catch` einer Mutation ihn
 * bedenkenlos `await`en kann.
 */
export async function readOwnerMustTransfer(
  err: unknown,
): Promise<{ merchantCenters: string[] } | null> {
  if (!err || typeof err !== 'object') return null;

  const context = (err as { context?: unknown }).context;
  if (!context || typeof context !== 'object') return null;

  const response = context as ResponseLike;
  if (response.status !== 409) return null;
  if (typeof response.json !== 'function') return null;

  try {
    const source = typeof response.clone === 'function' ? response.clone() : response;
    const body = await (source as { json: () => Promise<unknown> }).json();
    if (!body || typeof body !== 'object') return null;

    const errorCode = (body as { error?: unknown }).error;
    if (errorCode !== 'OWNER_MUST_TRANSFER') return null;

    const raw = (body as { merchant_centers?: unknown }).merchant_centers;
    const merchantCenters = Array.isArray(raw) ? raw.filter((m): m is string => typeof m === 'string') : [];
    return { merchantCenters };
  } catch {
    // Body nicht lesbar oder kein gueltiges JSON -- tolerieren, nicht werfen.
    return null;
  }
}

/**
 * Rohzeile von `merchant_center_members` (Migration
 * `20260923110000_merchant_center_members.sql`): ein Grant je (Merchant
 * Center, Nutzer), `owner` oder `member`. Seit Migration B (Task 15) kennt
 * `src/integrations/supabase/types.ts` die Tabelle; der generierte Typ fuehrt
 * `role`/`access_status` aber als `string`, dieser Typ haelt die engen
 * Wertemengen fest (CHECK-Constraints der Migration).
 *
 * Fix-Runde 1 (Minor 1): hiess bis dahin `McMember` und war ein toter Export
 * (kein Aufrufer im Repo). Umbenannt auf `McMemberRow`, weil Task 11 einen
 * eigenen, ANDERS geformten `McMember`-Typ definieren wird (UI-Sicht statt
 * DB-Zeile) -- zwei Typen mit demselben Namen in verschiedenen Dateien waeren
 * beim Lesen verwechselbar.
 */
export interface McMemberRow {
  id: string;
  merchant_center_id: string;
  user_id: string;
  google_account_id: string;
  role: 'owner' | 'member';
  access_status: 'active' | 'permission_denied';
  joined_at: string;
}

/**
 * Grenz-Guard fuer `merchant_center_members.role` (Fix-Runde 1, Minor 5):
 * liefert die Rolle nur bei einem der beiden bekannten Werte zurueck, sonst
 * `null`. Die Spalte traegt einen CHECK-Constraint auf genau diese zwei
 * Werte -- der generierte Supabase-Typ fuehrt die Spalte aber als `string`,
 * ein `as`-Cast wuerde also jeden Wert kommentarlos durchwinken. Aufrufer:
 * `MerchantCenters.tsx`, `useMcMembers.ts`.
 */
export function asMemberRole(value: unknown): McMemberRow['role'] | null {
  return value === 'owner' || value === 'member' ? value : null;
}

/**
 * Ein PostgREST-Zu-eins-Embed (hier: die Kante von `merchant_center_members`
 * zu `merchant_centers` ueber die FK `merchant_center_id`) kommt je nach
 * Client-Version und Anfrageform entweder als einzelnes Objekt oder als Array
 * mit hoechstens einem Element zurueck. Alle Leser der Mitgliedschafts-
 * Umstellung (Task 10) unwrappen defensiv darueber, statt die Form an jeder
 * Aufrufstelle neu zu raten -- und ein leeres/nicht sichtbares Embed (vor
 * Migration B fuer jede MC, die nur ueber `role='member'` erreichbar ist,
 * siehe `MC_TREE_SELECT` unten) wird zu `null`, nie zu einem Crash.
 */
export function firstEmbedded<T>(value: T | readonly T[] | null | undefined): T | null {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    return ((value as readonly T[])[0] as T | undefined) ?? null;
  }
  return value as T;
}

/**
 * Der eine gemeinsame Kern der "MCs eines Grants" Flatten/Dedupe-Schleife
 * (Fix-Runde 1, Important 1): bis dahin stand
 * `mc = firstEmbedded(row.X); if (!mc || seen.has(key)) continue; seen.add(key);
 * push(...)` woertlich kopiert in fuenf Dateien (`useContextTree.ts`,
 * `SelectFeedStep.tsx`, `SelectionContext.tsx`, `GoogleAccounts.tsx`,
 * `MerchantCenters.tsx`), aber nur eine Kopie hatte eigene Tests
 * (`useContextTree.test.ts` -> `buildTree`). `collectEmbedded` ist jetzt der
 * einzige Ort, an dem diese Schleife steht; die fuenf Aufrufer reichen nur
 * noch ihre eigene Embed- und Schluessel-Ableitung durch.
 *
 * - `getEmbed` liest das (moeglicherweise Objekt-oder-Array-foermige) Embed
 *   aus der Rohzeile; `firstEmbedded` entpackt es.
 * - Ein `null`-Embed (RLS-blockiert, vor Migration B fuer `role='member'`)
 *   ueberspringt die Zeile, ohne die uebrigen aufzuhalten.
 * - `keyOf` bekommt Zeile UND Embed -- die Ads-Zeilen-MC-Query in
 *   `GoogleAccounts.tsx` dedupliziert z.B. ueber `${google_account_id}:${mc.id}`
 *   statt nur `mc.id`, weil dieselbe MC unter zwei Konten je einmal stehen
 *   darf.
 * - Behaelt bei einem Schluessel-Konflikt die ERSTE Fundstelle und die
 *   Eingabereihenfolge -- keine implizite Sortierung.
 */
export function collectEmbedded<R, E>(
  rows: readonly R[],
  getEmbed: (row: R) => E | readonly E[] | null | undefined,
  keyOf: (row: R, embed: E) => string,
): Array<{ row: R; embed: E }> {
  const seen = new Set<string>();
  const result: Array<{ row: R; embed: E }> = [];
  for (const row of rows) {
    const embed = firstEmbedded(getEmbed(row));
    if (!embed) continue;
    const key = keyOf(row, embed);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ row, embed });
  }
  return result;
}

/**
 * "MCs eines Google-Kontos" heisst seit der Mitgliedschafts-Umstellung (Spec
 * `docs/superpowers/specs/2026-09-23-merchant-center-mitgliedschaft-design.md`)
 * "MCs, fuer die dieses Konto der Grant einer Mitgliedschaft ist" -- die
 * Regel steht hier als EIN Select-Fragment fuer `useContextTree`, den
 * einzigen Leser, der je MC den vollen Feed-Baum braucht (die uebrigen vier
 * Stellen der Umstellung selektieren schmalere Spaltenlisten und bauen ihr
 * eigenes Select, teils mit `!inner`). Bewusst OHNE `!inner`: vor Migration B
 * liefert die Einbettung fuer eine nur ueber `role='member'` erreichbare MC
 * nichts (die RLS-Kette von `merchant_centers` kennt bis dahin nur den
 * Eigentuemer) -- mit `!inner` wuerde das die ganze Mitgliedschaftszeile aus
 * dem Baum werfen, ohne `!inner` bleibt sie mit `merchant_centers: null`
 * stehen und wird beim Bauen des Baums uebersprungen (`firstEmbedded` + Null-
 * Filter in `useContextTree.ts:flattenMemberMcs`).
 */
export const MC_TREE_SELECT =
  'merchant_center_members(role, merchant_centers(id, merchant_center_id, name, parent_merchant_center_id, access_status, feeds(id, feed_id, display_name, feed_type, feed_input_type, target_country, content_language, last_synced_at, updated_at)))';
